import { computed } from "vue";
import { storeToRefs } from "pinia";
import { useAuthStateStore } from "@/stores/Utils/auth/AuthState";

// Frontegg's `useFeatureEntitlements` is a computed that calls `inject(authStateKey)`
// during evaluation; outside the original setup() that injection returns undefined,
// so the hook silently freezes on whatever value it produced first. We evaluate
// the same rule data ourselves against the reactive user from our Pinia auth store
// so canUseAiChat actually updates on tenant switch.

const AI_CHAT_FEATURE = "ai-rtlca-assistant";
const AI_CLASSIFICATION_FEATURE = "ai-classification";
const AI_EPD_INGESTION_FEATURE = "ai-epd-ingestion";

type FeatureFlagCondition = {
  attribute?: string;
  op?: string;
  negate?: boolean;
  value?: { list?: string[] };
};

type FeatureFlagRule = {
  treatment?: string;
  conditions?: FeatureFlagCondition[];
};

type FeatureFlag = {
  on?: boolean;
  defaultTreatment?: string;
  offTreatment?: string;
  rules?: FeatureFlagRule[];
};

type EntitlementUser = {
  tenantId?: string;
  entitlements?: { features?: Record<string, { featureFlag?: FeatureFlag } | undefined> };
};

// Exported for unit testing — this is the entitlement decision logic (fail-closed on unknown
// conditions, tenant in_list matching, on/off treatments) and is the highest-value thing to cover.
export function evaluateFlag(flag: FeatureFlag | undefined, tenantId: string | undefined): boolean {
  if (!flag) return false;
  if (!flag.on) return flag.offTreatment === "true";
  if (!tenantId) return flag.defaultTreatment === "true";
  for (const rule of flag.rules ?? []) {
    const conditions = rule.conditions ?? [];
    if (conditions.length === 0) continue;
    const matches = conditions.every((cond) => {
      if (cond.attribute === "frontegg.tenantId" && cond.op === "in_list") {
        const inList = !!cond.value?.list?.includes(tenantId);
        return cond.negate ? !inList : inList;
      }
      // Unsupported condition type — fail closed so a typo can't grant unintended access.
      return false;
    });
    if (matches) return rule.treatment === "true";
  }
  return flag.defaultTreatment === "true";
}

export const useTenantFeatureAccess = () => {
  const { user } = storeToRefs(useAuthStateStore());

  const canUseEpdExtraction = computed(() => {
    const flagUser = user.value as EntitlementUser | undefined;
    const flag = flagUser?.entitlements?.features?.[AI_EPD_INGESTION_FEATURE]?.featureFlag;
    return evaluateFlag(flag, flagUser?.tenantId);
  });

  const canUseAiChat = computed(() => {
    const u = user.value as EntitlementUser | undefined;
    const flag = u?.entitlements?.features?.[AI_CHAT_FEATURE]?.featureFlag;
    return evaluateFlag(flag, u?.tenantId);
  });

  const canUseAiClassification = computed(() => {
    const u = user.value as EntitlementUser | undefined;
    const flag = u?.entitlements?.features?.[AI_CLASSIFICATION_FEATURE]?.featureFlag;
    return evaluateFlag(flag, u?.tenantId);
  });

  return {
    canUseEpdExtraction,
    canUseAiChat,
    canUseAiClassification,
  };
};
